/** Asset counts per ratio lane, aggregated across every format on the page. */
export type LaneCounts = Record<'1x1' | '4x5' | '9x16', number>;

/**
 * Upper bound on cards across every card format (Snapchat Story allows the most,
 * at 20). Used only to enumerate the `card_image.N` keys; clearing a key that
 * was never set is a no-op.
 */
const MAX_CARDS_ANY_FORMAT = 20;

/**
 * Which validation issue keys a lane change has invalidated.
 *
 * `fieldIssues` is a snapshot of the last validation run and was never cleared
 * when assets changed, so a finding could outlive the problem: the Dynamic
 * Creative section showed "less than 2 images" against a ratio that visibly had
 * two, because that message was left over from an earlier run while the section
 * header counts were live. Rather than wiping everything (which would hide
 * findings the user hasn't addressed), only the keys downstream of the changed
 * lane are retired.
 *
 * Count rules are all keyed off lane sizes, so a lane whose size changed retires
 * its own key. `ratio_items.feed` subscribes to both feed crops and goes if
 * either moved. Per-card image findings go on any change, because the card
 * indices they name have shifted.
 *
 * @returns Issue keys to clear; empty when nothing relevant changed.
 */
export function staleIssueKeys(prev: LaneCounts, next: LaneCounts): string[] {
  const stale: string[] = [];
  for (const ratio of ['1x1', '4x5', '9x16'] as const) {
    if (prev[ratio] !== next[ratio]) stale.push(`ratio_items.${ratio}`);
  }
  if (stale.length === 0) return [];

  if (prev['1x1'] !== next['1x1'] || prev['4x5'] !== next['4x5']) {
    stale.push('ratio_items.feed');
  }
  stale.push(...Array.from({ length: MAX_CARDS_ANY_FORMAT }, (_, i) => `card_image.${i}`));
  return stale;
}
